import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError, downloadDispatchReport } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PaperPlaneTilt, Plus, Trash, PencilSimple, CurrencyInr, TrendUp, Package, Warning, DownloadSimple } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const TERMS = [15, 20, 30, 45];
const rupee = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const monthLabel = (m) => { try { const [y, mo] = m.split("-"); return new Date(y, mo - 1, 1).toLocaleString("en-IN", { month: "short", year: "2-digit" }); } catch { return m; } };

export default function Dispatch() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("dispatch", "edit");
  const confirm = useConfirm();
  const [tab, setTab] = useState("revenue");
  const [dispatches, setDispatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [summary, setSummary] = useState(null);

  const load = () => {
    api.get("/dispatches").then((r) => setDispatches(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/dispatches/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // ---- New dispatch dialog ----
  const [dOpen, setDOpen] = useState(false);
  const [dForm, setDForm] = useState({ date: today(), customer_id: "", product_id: "", quantity: "", rate: "", credit_terms: "30", notes: "" });
  const [savingD, setSavingD] = useState(false);
  const openDispatch = () => { setDForm({ date: today(), customer_id: "", product_id: "", quantity: "", rate: "", credit_terms: "30", notes: "" }); setDOpen(true); };
  const pickProduct = (id) => {
    const p = products.find((x) => x.id === id);
    setDForm((f) => ({ ...f, product_id: id, rate: f.rate || (p ? String(p.default_rate || "") : "") }));
  };
  const saveDispatch = async () => {
    if (!dForm.customer_id) return toast.error("Select a customer");
    if (!dForm.product_id) return toast.error("Select a product");
    if (!(Number(dForm.quantity) > 0)) return toast.error("Enter a quantity");
    if (!(Number(dForm.rate) >= 0) || dForm.rate === "") return toast.error("Enter a rate");
    setSavingD(true);
    try {
      await api.post("/dispatches", {
        date: dForm.date, customer_id: dForm.customer_id, product_id: dForm.product_id,
        quantity: Number(dForm.quantity), rate: Number(dForm.rate), credit_terms: Number(dForm.credit_terms), notes: dForm.notes,
      });
      toast.success("Dispatch recorded");
      setDOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingD(false); }
  };

  // ---- Credit dialog ----
  const [credRow, setCredRow] = useState(null);
  const [credAmt, setCredAmt] = useState("");
  const [credDate, setCredDate] = useState(today());
  const [credSaving, setCredSaving] = useState(false);
  const openCredit = (d) => { setCredRow(d); setCredAmt(String(d.pending)); setCredDate(today()); setCredSaving(false); };
  const saveCredit = async () => {
    const amt = Number(credAmt) || 0;
    if (amt <= 0) return toast.error("Enter an amount");
    setCredSaving(true);
    try {
      await api.post(`/dispatches/${credRow.id}/credit`, { amount: amt, date: credDate });
      toast.success(`${rupee(amt)} credit recorded`);
      setCredRow(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setCredSaving(false); }
  };

  const removeDispatch = async (d) => {
    if (!(await confirm({ title: "Delete Dispatch", description: `Delete ${d.dispatch_number}? This cannot be undone.`, confirmText: "Delete" }))) return;
    try { await api.delete(`/dispatches/${d.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const statusBadge = (d) => {
    if (d.status === "credited") return <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-[#16A34A] text-white">Credited</span>;
    if (d.overdue) return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase bg-[#DC2626] text-white"><Warning size={11} weight="bold" />Overdue</span>;
    if (d.status === "partial") return <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-[#EA580C] text-white">Partial</span>;
    return <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-zinc-200 text-zinc-600">Pending</span>;
  };

  const t = summary?.totals || { revenue: 0, credited: 0, pipeline: 0, count: 0 };

  const exportExcel = async () => {
    try { await downloadDispatchReport(); toast.success("Excel downloaded"); }
    catch { toast.error("Export failed"); }
  };

  return (
    <>
      <PageHeader title="Dispatch & Revenue" subtitle="Sales · Receivables · Pipeline">
        <Button variant="outline" onClick={exportExcel} data-testid="export-dispatch-btn" className="rounded-none border-zinc-300 font-bold uppercase tracking-wider text-xs">
          <DownloadSimple size={16} weight="bold" className="mr-1" /> Export Excel
        </Button>
        {canEdit && (
          <Button onClick={openDispatch} data-testid="new-dispatch-btn" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">
            <Plus size={16} weight="bold" className="mr-1" /> New Dispatch
          </Button>
        )}
      </PageHeader>

      <div className="p-4 sm:p-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none bg-zinc-100 mb-6">
            <TabsTrigger value="revenue" data-testid="tab-revenue" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white uppercase text-xs font-bold">Revenue</TabsTrigger>
            <TabsTrigger value="dispatches" data-testid="tab-dispatches" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white uppercase text-xs font-bold">Dispatches</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white uppercase text-xs font-bold">Products</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white uppercase text-xs font-bold">Customers</TabsTrigger>
          </TabsList>

          {/* ---------- REVENUE ---------- */}
          <TabsContent value="revenue" className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Total Revenue" value={rupee(t.revenue)} icon={TrendUp} accent="#16A34A" testid="stat-revenue" />
              <StatCard label="Credited" value={rupee(t.credited)} icon={CurrencyInr} accent="#0891B2" testid="stat-credited" />
              <StatCard label="Funds in Pipeline" value={rupee(t.pipeline)} icon={CurrencyInr} accent="#EA580C" testid="stat-pipeline" />
              <StatCard label="Dispatches" value={t.count} icon={PaperPlaneTilt} accent="#7C3AED" testid="stat-count" />
            </div>

            <div className="bg-white border border-zinc-300">
              <div className="px-6 py-4 border-b border-zinc-300 bg-zinc-50"><h3 className="font-heading font-bold uppercase tracking-tight text-sm">Monthly Revenue</h3></div>
              <div className="p-4" style={{ height: 300 }} data-testid="revenue-chart">
                {(summary?.monthly?.length ?? 0) === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-zinc-400">No dispatches yet</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={(summary.monthly || []).map((m) => ({ ...m, label: monthLabel(m.month) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v) => rupee(v)} />
                      <Legend />
                      <Bar dataKey="revenue" name="Revenue" fill="#EA580C" />
                      <Bar dataKey="credited" name="Credited" fill="#16A34A" />
                      <Bar dataKey="pipeline" name="Pipeline" fill="#0891B2" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <BreakdownTable title="Revenue by Product" rows={summary?.by_product || []} testid="by-product" />
              <div className="bg-white border border-zinc-300" data-testid="customer-statement">
                <div className="px-6 py-4 border-b border-zinc-300 bg-zinc-50"><h3 className="font-heading font-bold uppercase tracking-tight text-sm">Customer Statement</h3></div>
                {(summary?.by_customer?.length ?? 0) === 0 ? <p className="px-6 py-6 text-sm text-zinc-400">No data</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-white border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500">
                        <tr>
                          <th className="text-left px-4 py-2">Customer</th>
                          <th className="text-right px-4 py-2">Dispatched</th>
                          <th className="text-right px-4 py-2">Credited</th>
                          <th className="text-right px-4 py-2">Outstanding</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {summary.by_customer.map((c) => (
                          <tr key={c.name} data-testid={`statement-row-${c.name}`}>
                            <td className="px-4 py-2 text-zinc-800">{c.name} <span className="text-zinc-400 text-xs">· {c.count} dispatch{c.count === 1 ? "" : "es"}</span></td>
                            <td className="px-4 py-2 text-right font-mono font-bold">{rupee(c.revenue)}</td>
                            <td className="px-4 py-2 text-right font-mono text-[#16A34A]">{rupee(c.credited)}</td>
                            <td className={`px-4 py-2 text-right font-mono font-bold ${(c.outstanding || 0) > 0 ? "text-[#EA580C]" : "text-zinc-400"}`}>{rupee(c.outstanding)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white border border-zinc-300" data-testid="pipeline-list">
              <div className="px-6 py-4 border-b border-zinc-300 bg-zinc-50 flex items-center justify-between">
                <h3 className="font-heading font-bold uppercase tracking-tight text-sm">Funds in Pipeline</h3>
                <span className="font-mono text-sm font-bold text-[#EA580C]">{rupee(t.pipeline)}</span>
              </div>
              {(summary?.pipeline?.length ?? 0) === 0 ? (
                <p className="px-6 py-6 text-sm text-zinc-400">No pending receivables — everything credited.</p>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {summary.pipeline.map((d) => (
                    <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-3" data-testid={`pipeline-row-${d.id}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="font-mono text-xs font-bold text-primary">{d.dispatch_number}</span>
                        <span className="text-sm text-zinc-900">{d.customer_name}</span>
                        <span className="text-[11px] text-zinc-400">{d.product_name} · {d.quantity} {d.unit}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {d.due_date && <span className={`px-2 py-0.5 text-[10px] font-bold uppercase text-white ${d.overdue ? "bg-[#DC2626]" : "bg-[#0891B2]"}`}>{d.overdue ? "Overdue" : "Expected"} {d.due_date}</span>}
                        <span className="font-mono text-sm font-bold text-[#EA580C]">{rupee(d.pending)}</span>
                        {canEdit && <Button size="sm" onClick={() => openCredit(d)} data-testid={`credit-${d.id}`} className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white text-[11px] uppercase font-bold h-7">Record Credit</Button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ---------- DISPATCHES ---------- */}
          <TabsContent value="dispatches">
            <div className="bg-white border border-zinc-300" data-testid="dispatch-list">
              {dispatches.length === 0 ? (
                <p className="px-6 py-8 text-sm text-zinc-400 text-center">No dispatches yet. Click "New Dispatch" to record one.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 border-b border-zinc-300 text-[10px] uppercase tracking-wider text-zinc-500">
                      <tr>
                        <th className="text-left px-4 py-3">Dispatch</th>
                        <th className="text-left px-4 py-3">Date</th>
                        <th className="text-left px-4 py-3">Customer</th>
                        <th className="text-left px-4 py-3">Product</th>
                        <th className="text-right px-4 py-3">Qty</th>
                        <th className="text-right px-4 py-3">Rate</th>
                        <th className="text-right px-4 py-3">Amount</th>
                        <th className="text-right px-4 py-3">Credited</th>
                        <th className="text-right px-4 py-3">Pending</th>
                        <th className="text-left px-4 py-3">Due</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {dispatches.map((d) => (
                        <tr key={d.id} data-testid={`dispatch-row-${d.id}`} className="hover:bg-zinc-50">
                          <td className="px-4 py-3 font-mono text-xs font-bold text-primary">{d.dispatch_number}</td>
                          <td className="px-4 py-3 text-zinc-500">{d.date}</td>
                          <td className="px-4 py-3">{d.customer_name}</td>
                          <td className="px-4 py-3">{d.product_name}</td>
                          <td className="px-4 py-3 text-right font-mono">{d.quantity} {d.unit}</td>
                          <td className="px-4 py-3 text-right font-mono">{rupee(d.rate)}</td>
                          <td className="px-4 py-3 text-right font-mono font-bold">{rupee(d.amount)}</td>
                          <td className="px-4 py-3 text-right font-mono text-[#16A34A]">{rupee(d.amount_credited)}</td>
                          <td className={`px-4 py-3 text-right font-mono font-bold ${d.pending > 0 ? "text-[#EA580C]" : "text-zinc-400"}`}>{rupee(d.pending)}</td>
                          <td className={`px-4 py-3 ${d.overdue ? "text-[#DC2626] font-bold" : "text-zinc-500"}`}>{d.due_date || "—"}</td>
                          <td className="px-4 py-3">{statusBadge(d)}</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {canEdit && d.pending > 0 && <Button size="sm" onClick={() => openCredit(d)} data-testid={`row-credit-${d.id}`} className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white text-[10px] uppercase font-bold h-7 mr-1">Credit</Button>}
                            {canEdit && <button onClick={() => removeDispatch(d)} data-testid={`delete-dispatch-${d.id}`} className="text-zinc-400 hover:text-[#DC2626] p-1.5"><Trash size={15} weight="bold" /></button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ---------- PRODUCTS ---------- */}
          <TabsContent value="products">
            <ProductManager products={products} canEdit={canEdit} confirm={confirm} reload={load} />
          </TabsContent>

          {/* ---------- CUSTOMERS ---------- */}
          <TabsContent value="customers">
            <CustomerManager customers={customers} canEdit={canEdit} confirm={confirm} reload={load} />
          </TabsContent>
        </Tabs>
      </div>

      {/* New Dispatch dialog */}
      <Dialog open={dOpen} onOpenChange={setDOpen}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-lg" data-testid="dispatch-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">New Dispatch</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] font-bold uppercase text-zinc-500">Dispatch Date</Label>
                <Input type="date" data-testid="dispatch-date" value={dForm.date} onChange={(e) => setDForm({ ...dForm, date: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold uppercase text-zinc-500">Credit Terms</Label>
                <Select value={dForm.credit_terms} onValueChange={(v) => setDForm({ ...dForm, credit_terms: v })}>
                  <SelectTrigger data-testid="dispatch-terms" className="rounded-none border-zinc-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{TERMS.map((d) => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase text-zinc-500">Customer</Label>
              <Select value={dForm.customer_id} onValueChange={(v) => setDForm({ ...dForm, customer_id: v })}>
                <SelectTrigger data-testid="dispatch-customer" className="rounded-none border-zinc-300"><SelectValue placeholder={customers.length ? "Select customer" : "Add a customer first"} /></SelectTrigger>
                <SelectContent className="rounded-none max-h-64">{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase text-zinc-500">Product</Label>
              <Select value={dForm.product_id} onValueChange={pickProduct}>
                <SelectTrigger data-testid="dispatch-product" className="rounded-none border-zinc-300"><SelectValue placeholder={products.length ? "Select product" : "Add a product first"} /></SelectTrigger>
                <SelectContent className="rounded-none max-h-64">{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] font-bold uppercase text-zinc-500">Quantity</Label>
                <Input type="number" min="0" placeholder="0" data-testid="dispatch-qty" value={dForm.quantity} onChange={(e) => setDForm({ ...dForm, quantity: e.target.value })} className="rounded-none border-zinc-300 font-mono text-right focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold uppercase text-zinc-500">Rate / piece (₹)</Label>
                <Input type="number" min="0" placeholder="0" data-testid="dispatch-rate" value={dForm.rate} onChange={(e) => setDForm({ ...dForm, rate: e.target.value })} className="rounded-none border-zinc-300 font-mono text-right focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
            </div>
            <div className="flex justify-between items-center bg-zinc-50 border border-zinc-200 px-3 py-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Amount</span>
              <span className="font-mono text-lg font-bold text-zinc-900" data-testid="dispatch-amount">{rupee((Number(dForm.quantity) || 0) * (Number(dForm.rate) || 0))}</span>
            </div>
            <Textarea data-testid="dispatch-notes" value={dForm.notes} onChange={(e) => setDForm({ ...dForm, notes: e.target.value })} placeholder="Notes (optional)" className="rounded-none border-zinc-300 text-sm" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDOpen(false)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={saveDispatch} disabled={savingD} data-testid="dispatch-confirm" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">{savingD ? "Saving…" : "Record Dispatch"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit dialog */}
      <Dialog open={!!credRow} onOpenChange={(o) => { if (!o) setCredRow(null); }}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-md" data-testid="credit-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Record Credit</DialogTitle></DialogHeader>
          {credRow && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-zinc-500">{credRow.dispatch_number} · {credRow.customer_name} · Pending <span className="font-mono text-[#EA580C]">{rupee(credRow.pending)}</span></p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase text-[#16A34A]">Amount (₹)</Label>
                  <Input type="number" min="0" data-testid="credit-amount" value={credAmt} onChange={(e) => setCredAmt(e.target.value)} className="rounded-none border-zinc-300 font-mono text-right focus-visible:ring-2 focus-visible:ring-[#16A34A]" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase text-zinc-500">Date</Label>
                  <Input type="date" data-testid="credit-date" value={credDate} onChange={(e) => setCredDate(e.target.value)} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCredRow(null)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={saveCredit} disabled={credSaving} data-testid="credit-confirm" className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white font-bold uppercase tracking-wider">{credSaving ? "Saving…" : "Record Credit"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="bg-white border border-zinc-300 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{label}</p>
        <Icon size={18} weight="bold" style={{ color: accent }} />
      </div>
      <p className="font-heading text-xl font-black tracking-tight text-zinc-900">{value}</p>
    </div>
  );
}

function BreakdownTable({ title, rows, testid }) {
  return (
    <div className="bg-white border border-zinc-300" data-testid={testid}>
      <div className="px-6 py-4 border-b border-zinc-300 bg-zinc-50"><h3 className="font-heading font-bold uppercase tracking-tight text-sm">{title}</h3></div>
      {rows.length === 0 ? <p className="px-6 py-6 text-sm text-zinc-400">No data</p> : (
        <div className="divide-y divide-zinc-100">
          {rows.map((r) => (
            <div key={r.name} className="flex items-center justify-between px-6 py-2.5 text-sm">
              <span className="text-zinc-800">{r.name} <span className="text-zinc-400 text-xs">· {r.qty}</span></span>
              <span className="font-mono font-bold">₹{(Number(r.revenue) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductManager({ products, canEdit, confirm, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", default_rate: "", unit: "PCS" });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const openNew = () => { setForm({ name: "", default_rate: "", unit: "PCS" }); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ name: p.name, default_rate: String(p.default_rate ?? ""), unit: p.unit || "PCS" }); setEditId(p.id); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Product name is required");
    setSaving(true);
    const payload = { name: form.name.trim(), default_rate: Number(form.default_rate) || 0, unit: form.unit || "PCS" };
    try {
      if (editId) await api.put(`/products/${editId}`, payload); else await api.post("/products", payload);
      toast.success(editId ? "Product updated" : "Product added");
      setOpen(false); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const remove = async (p) => {
    if (!(await confirm({ title: "Delete Product", description: `Delete ${p.name}?`, confirmText: "Delete" }))) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); reload(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-zinc-300" data-testid="product-manager">
      <div className="px-6 py-4 border-b border-zinc-300 bg-zinc-50 flex items-center justify-between">
        <h3 className="font-heading font-bold uppercase tracking-tight text-sm flex items-center gap-2"><Package size={16} weight="bold" /> Products</h3>
        {canEdit && <Button size="sm" onClick={openNew} data-testid="new-product-btn" className="rounded-none bg-primary hover:bg-[#C2410C] text-white text-xs uppercase font-bold"><Plus size={14} weight="bold" className="mr-1" />Add Product</Button>}
      </div>
      {products.length === 0 ? <p className="px-6 py-6 text-sm text-zinc-400">No products yet.</p> : (
        <div className="divide-y divide-zinc-100">
          {products.map((p) => (
            <div key={p.id} className="flex items-center justify-between px-6 py-3" data-testid={`product-row-${p.id}`}>
              <div><span className="text-sm font-medium text-zinc-900">{p.name}</span><span className="text-xs text-zinc-400 ml-2">default ₹{p.default_rate} / {p.unit}</span></div>
              {canEdit && <div className="flex items-center gap-2">
                <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="text-zinc-400 hover:text-primary p-1.5"><PencilSimple size={15} weight="bold" /></button>
                <button onClick={() => remove(p)} data-testid={`delete-product-${p.id}`} className="text-zinc-400 hover:text-[#DC2626] p-1.5"><Trash size={15} weight="bold" /></button>
              </div>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-md" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">{editId ? "Edit" : "Add"} Product</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1"><Label className="text-[10px] font-bold uppercase text-zinc-500">Product / Model Name</Label>
              <Input data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-[10px] font-bold uppercase text-zinc-500">Default Rate (₹)</Label>
                <Input type="number" min="0" data-testid="product-rate" value={form.default_rate} onChange={(e) => setForm({ ...form, default_rate: e.target.value })} className="rounded-none border-zinc-300 font-mono text-right focus-visible:ring-2 focus-visible:ring-primary" /></div>
              <div className="space-y-1"><Label className="text-[10px] font-bold uppercase text-zinc-500">Unit</Label>
                <Input data-testid="product-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="product-confirm" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CustomerManager({ customers, canEdit, confirm, reload }) {
  const empty = { name: "", gst_number: "", contact_person: "", email: "", phone: "", address: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c }); setEditId(c.id); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Customer name is required");
    setSaving(true);
    const payload = { name: form.name.trim(), gst_number: form.gst_number || "", contact_person: form.contact_person || "", email: form.email || "", phone: form.phone || "", address: form.address || "" };
    try {
      if (editId) await api.put(`/customers/${editId}`, payload); else await api.post("/customers", payload);
      toast.success(editId ? "Customer updated" : "Customer added");
      setOpen(false); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const remove = async (c) => {
    if (!(await confirm({ title: "Delete Customer", description: `Delete ${c.name}?`, confirmText: "Delete" }))) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Deleted"); reload(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-zinc-300" data-testid="customer-manager">
      <div className="px-6 py-4 border-b border-zinc-300 bg-zinc-50 flex items-center justify-between">
        <h3 className="font-heading font-bold uppercase tracking-tight text-sm">Customers</h3>
        {canEdit && <Button size="sm" onClick={openNew} data-testid="new-customer-btn" className="rounded-none bg-primary hover:bg-[#C2410C] text-white text-xs uppercase font-bold"><Plus size={14} weight="bold" className="mr-1" />Add Customer</Button>}
      </div>
      {customers.length === 0 ? <p className="px-6 py-6 text-sm text-zinc-400">No customers yet.</p> : (
        <div className="divide-y divide-zinc-100">
          {customers.map((c) => (
            <div key={c.id} className="flex items-center justify-between px-6 py-3" data-testid={`customer-row-${c.id}`}>
              <div>
                <span className="text-sm font-medium text-zinc-900">{c.name}</span>
                {c.gst_number && <span className="text-xs text-zinc-400 ml-2 font-mono">{c.gst_number}</span>}
                {(c.contact_person || c.phone) && <p className="text-[11px] text-zinc-400">{[c.contact_person, c.phone].filter(Boolean).join(" · ")}</p>}
              </div>
              {canEdit && <div className="flex items-center gap-2">
                <button onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`} className="text-zinc-400 hover:text-primary p-1.5"><PencilSimple size={15} weight="bold" /></button>
                <button onClick={() => remove(c)} data-testid={`delete-customer-${c.id}`} className="text-zinc-400 hover:text-[#DC2626] p-1.5"><Trash size={15} weight="bold" /></button>
              </div>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-md" data-testid="customer-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">{editId ? "Edit" : "Add"} Customer</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1"><Label className="text-[10px] font-bold uppercase text-zinc-500">Name</Label>
              <Input data-testid="customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-[10px] font-bold uppercase text-zinc-500">GSTIN</Label>
                <Input data-testid="customer-gst" value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} className="rounded-none border-zinc-300 font-mono focus-visible:ring-2 focus-visible:ring-primary" /></div>
              <div className="space-y-1"><Label className="text-[10px] font-bold uppercase text-zinc-500">Phone</Label>
                <Input data-testid="customer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-[10px] font-bold uppercase text-zinc-500">Contact Person</Label>
                <Input data-testid="customer-contact" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" /></div>
              <div className="space-y-1"><Label className="text-[10px] font-bold uppercase text-zinc-500">Email</Label>
                <Input data-testid="customer-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" /></div>
            </div>
            <div className="space-y-1"><Label className="text-[10px] font-bold uppercase text-zinc-500">Address</Label>
              <Textarea data-testid="customer-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none border-zinc-300 text-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="customer-confirm" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
